import { Model, modelRegistry } from './Model';
import { Order, Role, Table, UserTransaction, Session, Shift } from '.';
import { OrderFlagType, UserTransactionType } from '../types';
import { UserService } from '../services';
import Money from '../utilities/Money';
import HashGen from '../utilities/HashGen';
import Store from '../wrappers/Store';
import moment from 'moment-timezone';
export class User extends Model {
    static entity = 'users';
    id = null;
    hash = '';
    username = '';
    name = '';
    pin = null;
    pin_alt = null;
    role_id = null;
    status = null;
    receipts_allowed = true;
    created_at = null;
    updated_at = null;
    role;
    static fields() {
        return {
            id: this.attr(null),
            hash: this.attr(HashGen.regular()),
            username: this.attr(''),
            name: this.attr(''),
            pin: this.attr(null),
            pin_alt: this.attr(null),
            role_id: this.attr(null),
            status: this.attr(null),
            receipts_allowed: this.attr(true, value => !!value),
            charged: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            deposit: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            cash: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            created_at: this.attr(null),
            updated_at: this.attr(null),
            role: this.attr(undefined, value => value ? new Role(value) : undefined),
        };
    }
    constructor(record) {
        super();
        this.$fill(record);
    }
    $duplicate() {
        return new User(this.$toJson());
    }
    /**
     * Add User's deposit
     */
    async addDeposit(amount) {
        let amountToAdd = Money.parse(amount).toFormat('0.0000');
        return this.addTransaction(amountToAdd, UserTransactionType.deposit);
    }
    /**
     * Add User's charge
     */
    async addCharge(amount) {
        let amountToAdd = Money.parse(amount).toFormat('0.0000');
        return this.addTransaction(amountToAdd, UserTransactionType.charge);
    }
    /**
     * Add the transaction denoting the amount that user transfered to the table
     */
    async addTransfered(amount) {
        let amountToAdd = Money.parse(amount).toFormat('0.0000');
        return this.addTransaction(amountToAdd, UserTransactionType.transfer);
    }
    /**
     * Add User Transaction
     */
    async addTransaction(amount, type) {
        if (!Store.get('tenant')) {
            return { success: false };
        }
        let curShift = Shift.current();
        if (!curShift)
            return { success: false };
        let transaction = new UserTransaction({
            hash: HashGen.regular(),
            user_id: this.id,
            shift_id: curShift.id,
            shift_hash: curShift.hash,
            amount,
            type,
            issued_at: moment().utc().format(UserTransaction.DATE_FORMAT),
        });
        return transaction.sync();
    }
    /**
     * Clear user's debt
     */
    async clearDebt() {
        let chargeToAdd = this.getDebt().toUnit();
        return this.addCharge(chargeToAdd);
    }
    /**
     * Open a new session.
     */
    async clockIn() {
        return Session.open(this);
    }
    /**
     * Close the existing session.
     */
    async clockOut() {
        const session = this.getSession();
        if (!session)
            return { success: false };
        return session.$duplicate().close();
    }
    /**
     * Make the session the server returned at login this user's open session. Another synced one
     * still open here was closed or replaced on the server (a manager closed it, or the login closed
     * it as stale) and must not take the next clock-out. None is returned when the login does not
     * clock in (the admin panel).
     */
    setSession(session) {
        if (!session)
            return;
        Session.all()
            .filter(s => s.user_id === this.id && !!s.id && !s.clock_out && s.hash !== session.hash)
            .forEach(s => s.$delete());
        Session.insert({ data: [session] });
    }
    /**
     * The user's open session — the latest, should this device still hold an older one open.
     */
    getSession() {
        return Session.all()
            .filter(session => session.user_id === this.id && !!session.clock_in && !session.clock_out)
            .sort((a, b) => moment.utc(b.clock_in).valueOf() - moment.utc(a.clock_in).valueOf())[0];
    }
    getCharged() {
        const shift = Shift.current();
        const transactions = shift ? shift.getUserTransactions() : [];
        return transactions.filter(t => t.user_id == this.id)
            .filter(t => t.type === UserTransactionType.charge)
            .reduce((acc, cur) => acc.add(cur.getAmount()), Money.parse(0));
    }
    getDeposit() {
        const shift = Shift.current();
        const transactions = shift ? shift.getUserTransactions() : [];
        return transactions.filter(t => t.user_id == this.id)
            .filter(t => t.type === UserTransactionType.deposit)
            .reduce((acc, cur) => acc.add(cur.getAmount()), Money.parse(0));
    }
    getCash() {
        const shift = Shift.current();
        const orders = shift ? shift.getOrders() : [];
        const userOrders = orders.filter(order => {
            const userId = Number(order.getFlag(OrderFlagType.clearDebtForUser)[0]) || order.user_id;
            return userId === this.id;
        });
        return userOrders.filter(order => order.isCashExchanged())
            .reduce((acc, cur) => acc.add(cur.getPaidCash()), Money.parse(0));
    }
    getTransfered() {
        const shift = Shift.current();
        const transactions = shift ? shift.getUserTransactions() : [];
        return transactions.filter(t => t.user_id == this.id)
            .filter(t => t.type === UserTransactionType.transfer)
            .reduce((acc, cur) => acc.add(cur.getAmount()), Money.parse(0));
    }
    getDebt() {
        return this.getDeposit()
            .add(this.getTransfered())
            .add(this.getCash())
            .subtract(this.getCharged());
    }
    getTables() {
        const tables = Table.all();
        const unpaidOrders = Order.onTables();
        return tables.filter(table => {
            const tableOrders = unpaidOrders.filter(o => o.table_id === table.id);
            if (!tableOrders.length)
                return false;
            return tableOrders.some(order => order.user_id === this.id);
        });
    }
    canAccessTable(table, ordersOnTable) {
        if (UserService.allows('allow_enter_all_tables'))
            return true;
        if (table.type === 'limited')
            return false;
        const tableOwner = User.getTableOwner(ordersOnTable);
        if (!tableOwner)
            return true;
        return tableOwner.id === this.id;
    }
    static getTableOwner(ordersOnTable) {
        if (!ordersOnTable.length)
            return undefined;
        const nonTransferedOrders = ordersOnTable.filter(order => !order.getFlag(OrderFlagType.tableTransfer).length);
        if (nonTransferedOrders.length)
            return nonTransferedOrders[0].getUser();
        return ordersOnTable[0].getUser();
    }
    /**
     * Get the currently logged in user
     */
    static current() {
        const loggedUser = Store.get('user');
        if (!loggedUser)
            return undefined;
        return User.find(loggedUser.id);
    }
}
export default User;
modelRegistry.register('User', User);
//# sourceMappingURL=User.js.map